// Labels, colours and small pure helpers shared by the Video QC tab.
// Colours follow the BrandConnect palette (see CONSENT in HybridCampaignDashboard).

export const VERDICT = {
    PASS: { label: 'Pass', bg: '#d1fae5', color: '#065f46', icon: '✓' },
    FAIL: { label: 'Fail', bg: '#fee2e2', color: '#991b1b', icon: '✗' },
    'NEEDS-REVIEW': { label: 'Needs review', bg: '#fef3c7', color: '#92400e', icon: '⚠' },
};

export const STATUS = {
    pending: { label: 'Not checked', bg: '#f3f4f6', color: '#6b7280' },
    queued: { label: 'Queued', bg: '#eff6ff', color: '#1d4ed8' },
    processing: { label: 'Checking', bg: '#eef2ff', color: '#4f46e5' },
    done: { label: 'Checked', bg: '#f0fdf4', color: '#16a34a' },
    error: { label: 'Failed', bg: '#fee2e2', color: '#991b1b' },
};

// How serious something the reviewer found is. One palette for result
// cards, timeline markers and the issue strip, so a colour means one thing.
// `ink` is the text colour on `soft` (WCAG AA).
export const TONE = {
    bad: { color: '#dc2626', soft: '#fee2e2', ink: '#991b1b' },
    flag: { color: '#7c3aed', soft: '#ede9fe', ink: '#5b21b6' },
    warn: { color: '#d97706', soft: '#fef3c7', ink: '#92400e' },
    good: { color: '#16a34a', soft: '#dcfce7', ink: '#166534' },
};

export const RESULT = {
    met: { label: 'Met', tone: 'good', color: '#065f46' },
    missed: { label: 'Missed', tone: 'bad', color: '#991b1b' },
    unclear: { label: 'Unclear', tone: 'warn', color: '#92400e' },
};

export const RESULT_TONE = { met: 'good', missed: 'bad', unclear: 'warn' };

export const DIMENSIONS = [
    { id: 'spoken', label: 'Spoken', hint: 'What must or must not be said' },
    { id: 'visual', label: 'Visual', hint: 'Product, logo, on-screen text, setting' },
    { id: 'format', label: 'Format', hint: 'Length, aspect ratio, captions' },
    { id: 'tone', label: "Tone & do's/don'ts", hint: 'Judged across the whole video' },
];

export const FEEDBACK = {
    good: { label: 'Good', color: '#16a34a' },
    warn: { label: 'Check', color: '#d97706' },
    bad: { label: 'Problem', color: '#dc2626' },
};

export const SEVERITY = {
    high: { bg: '#fee2e2', color: '#991b1b' },
    medium: { bg: '#fef3c7', color: '#92400e' },
    low: { bg: '#f3f4f6', color: '#374151' },
};

export const isRunning = (status) => status === 'queued' || status === 'processing';

// The QC service's run stages, in order: `video.stage` while a run is queued
// or processing. "Done" is the end state, not a stage.
export const QC_STAGES = ['Queued', 'Downloading video', 'Reading format', 'Extracting frames', 'Transcribing audio', 'Scoring against the brief'];

// Where a running video is on QC_STAGES. -1 when it isn't running, or the
// service reports a stage this page doesn't know (shown as text instead).
export function stageIndex(status, stage) {
    if (status === 'queued') return 0;
    if (status !== 'processing') return -1;
    const wanted = String(stage || '').trim().toLowerCase();
    return QC_STAGES.findIndex((s) => s.toLowerCase() === wanted);
}

export const youtubeUrl = (videoId) => (videoId ? `https://www.youtube.com/watch?v=${videoId}` : null);

export const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const countResults = (results) => (results || []).reduce(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }),
    { met: 0, missed: 0, unclear: 0 },
);

// Everything a reviewer has to look at: items not met plus outliers.
export const issueCount = (check) => {
    if (!check) return null;
    const { missed, unclear } = countResults(check.results);
    return missed + unclear + (check.outliers || []).length;
};

// List rows only carry a check summary; use its counts when present.
export const summaryIssues = (latest) => {
    if (!latest) return null;
    if (latest.counts) return (latest.counts.missed || 0) + (latest.counts.unclear || 0) + (latest.counts.outliers || 0);
    return latest.results ? issueCount(latest) : null;
};

// Same rule as the QC service, used only to label sample data.
export const verdictFor = (results) => {
    if (!results.length) return 'NEEDS-REVIEW';
    if (results.some((r) => r.critical && r.status === 'missed')) return 'FAIL';
    return results.every((r) => r.status === 'met') ? 'PASS' : 'NEEDS-REVIEW';
};

const STATUS_ORDER = { missed: 0, unclear: 1, met: 2 };

// Results for review: missed first, then unclear, then met; critical items
// first within each. A new array, the input is left as it is.
export const orderResults = (results) => [...(results || [])].sort((a, b) => (
    (STATUS_ORDER[a.status] ?? 1) - (STATUS_ORDER[b.status] ?? 1) || Number(Boolean(b.critical)) - Number(Boolean(a.critical))
));

export const groupByDimension = (results) => DIMENSIONS
    .map((d) => ({ ...d, items: (results || []).filter((r) => (r.dimension || 'visual') === d.id) }))
    .filter((g) => g.items.length > 0);

export const feedbackByTs = (check) => new Map((check?.frame_feedback || []).map((f) => [f.ts, f]));

const newer = (a, b) => (String(a.created_at || '') >= String(b.created_at || '') ? a : b);

// One row per campaign video (matched to its newest QC record by
// external_ref), followed by drafts: QC videos not tied to a listed campaign
// video. Older duplicates for the same campaign video are not shown.
export function buildRows(campaignVideos = [], qcVideos = []) {
    const byRef = qcVideos.filter((q) => q.external_ref).reduce((map, q) => {
        const key = String(q.external_ref);
        return new Map(map).set(key, map.has(key) ? newer(q, map.get(key)) : q);
    }, new Map());
    const linked = campaignVideos.map((v) => {
        const qc = byRef.get(String(v.id)) || null;
        return {
            key: `video-${v.id}`,
            title: v.title || v.youtubeVideoId || 'Untitled video',
            subtitle: [v.platform, v.creatorName || v.channelId, v.publishedAt].filter(Boolean).join(' · ') || '—',
            source: v.source || youtubeUrl(v.youtubeVideoId),
            platform: v.platform || '',
            campaignVideoId: String(v.id),
            isDraft: false,
            qc,
        };
    });
    const campaignRefs = new Set(campaignVideos.map((v) => String(v.id)));
    const drafts = qcVideos.filter((q) => !q.external_ref || !campaignRefs.has(String(q.external_ref))).map((q) => ({
        key: `draft-${q.id}`,
        title: q.title || 'Draft video',
        subtitle: 'Draft · not linked to a deal',
        source: q.source,
        platform: '',
        campaignVideoId: null,
        isDraft: true,
        qc: q,
    }));
    return [...linked, ...drafts];
}

export function summarize(rows) {
    const verdicts = rows.map((r) => r.qc?.latest_check?.verdict).filter(Boolean);
    return {
        total: rows.length,
        PASS: verdicts.filter((v) => v === 'PASS').length,
        FAIL: verdicts.filter((v) => v === 'FAIL').length,
        'NEEDS-REVIEW': verdicts.filter((v) => v === 'NEEDS-REVIEW').length,
    };
}
